import * as React from 'react'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import EmptyState from './EmptyState'

export interface ColumnaTabla<T> {
  clave: string
  titulo: string
  /** Qué se pinta en la celda. Sin esto, el valor crudo de `fila[clave]`. */
  celda?: (fila: T, indice: number) => React.ReactNode
  /** Números y dinero a la derecha, siempre. Leer una columna de cifras desalineada cuesta. */
  alineacion?: 'izquierda' | 'derecha' | 'centro'
  /** Columnas que se esconden en móvil. La clave va al bloque plegado de cada fila. */
  ocultaEnMovil?: boolean
  anchoClase?: string
  /**
   * Papel de la columna en la TARJETA del teléfono (con `tarjetasEnMovil`): `titulo` arriba a la
   * izquierda, `valor` (dinero, estado) arriba a la derecha, `meta` en líneas pequeñas debajo,
   * `acciones` en la fila de botones del pie y `oculta` no aparece en el teléfono.
   */
  prioridad?: 'titulo' | 'valor' | 'meta' | 'acciones' | 'oculta'
}

export interface DataTableProps<T> {
  columnas: ReadonlyArray<ColumnaTabla<T>>
  filas: readonly T[]
  claveDeFila: (fila: T, indice: number) => string
  cargando?: boolean
  onFilaClick?: (fila: T) => void
  /** Qué enseñar cuando no hay filas. Un `<EmptyState>` con su propia explicación. */
  vacio?: React.ReactNode
  /** Resalta filas que piden atención (una hoja con errores, un cliente sin NIT). */
  filaDestacada?: (fila: T) => boolean
  className?: string
  etiquetaTabla?: string
  /**
   * En el teléfono (< md) cada fila se pinta como TARJETA según la `prioridad` de las columnas, en
   * vez de una tabla que se arrastra de lado. Desde `md`, tabla normal.
   */
  tarjetasEnMovil?: boolean
}

const ALINEACION = {
  izquierda: 'text-left',
  derecha: 'text-right',
  centro: 'text-center',
} as const

/**
 * Tabla de datos con una sola cara en todo el portal.
 *
 * Cada listado traía la suya: cabeceras distintas, cero estado de carga, y en móvil una
 * tabla de siete columnas que obligaba a arrastrar de lado. Aquí las columnas marcadas se
 * esconden en pantalla pequeña y bajan a una lista de `clave: valor` dentro de la fila, que
 * es la única forma de que una tabla ancha sirva en un móvil.
 *
 * Lo que NO hace, a propósito: ordenar, filtrar y paginar. Eso lo decide cada pantalla —el
 * portal ya tiene `Pagination`— y meterlo aquí convertiría el componente en un framework
 * que nadie querría tocar.
 */
export function DataTable<T>({
  columnas,
  filas,
  claveDeFila,
  cargando = false,
  onFilaClick,
  vacio,
  filaDestacada,
  className,
  etiquetaTabla,
  tarjetasEnMovil = false,
}: DataTableProps<T>) {
  if (cargando) {
    return (
      <div className={cn('space-y-2', className)} aria-busy="true" aria-live="polite">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-11 w-full rounded-md" />
        ))}
      </div>
    )
  }

  if (filas.length === 0) {
    return <>{vacio ?? <EmptyState titulo="No hay nada que mostrar todavía." />}</>
  }

  const visiblesMovil = columnas.filter((c) => !c.ocultaEnMovil)
  const plegadasMovil = columnas.filter((c) => c.ocultaEnMovil)
  const valor = (col: ColumnaTabla<T>, fila: T, i: number): React.ReactNode =>
    col.celda ? col.celda(fila, i) : ((fila as Record<string, unknown>)[col.clave] as React.ReactNode)

  const porPrioridad = (p: ColumnaTabla<T>['prioridad']) => columnas.filter((c) => c.prioridad === p)
  const tarjetas = tarjetasEnMovil && (
    <ul className="space-y-2.5 md:hidden" aria-label={etiquetaTabla}>
      {filas.map((fila, i) => {
        const titulo = porPrioridad('titulo')
        const valorCol = porPrioridad('valor')
        const meta = porPrioridad('meta')
        const acciones = porPrioridad('acciones')
        return (
          <li
            key={claveDeFila(fila, i)}
            onClick={onFilaClick ? () => onFilaClick(fila) : undefined}
            className={cn(
              'rounded-lg border border-line bg-card p-3.5 shadow-sm',
              filaDestacada?.(fila) && 'border-warning/40 bg-warning-50 dark:bg-warning/10',
              onFilaClick && 'cursor-pointer active:bg-surface-muted'
            )}
          >
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1 text-body-sm">{titulo.map((c) => <div key={c.clave}>{valor(c, fila, i)}</div>)}</div>
              {valorCol.length > 0 && (
                <div className="flex shrink-0 flex-col items-end gap-1 text-right text-body-sm">
                  {valorCol.map((c) => <div key={c.clave}>{valor(c, fila, i)}</div>)}
                </div>
              )}
            </div>
            {meta.length > 0 && (
              <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1">
                {meta.map((c) => (
                  <div key={c.clave} className="min-w-0">
                    <dt className="text-micro uppercase tracking-wide text-subtle">{c.titulo}</dt>
                    <dd className="truncate text-body-sm text-foreground">{valor(c, fila, i)}</dd>
                  </div>
                ))}
              </dl>
            )}
            {acciones.length > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3" onClick={(e) => e.stopPropagation()}>
                {acciones.map((c) => <React.Fragment key={c.clave}>{valor(c, fila, i)}</React.Fragment>)}
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )

  return (
    <>
    {tarjetas}
    {/* El contenedor scrollea solo: es lo que impide que la página entera se mueva de lado. */}
    <div className={cn('w-full overflow-x-auto rounded-lg border border-line', tarjetasEnMovil && 'hidden md:block', className)}>
      <table className="w-full border-collapse text-body" aria-label={etiquetaTabla}>
        <thead>
          <tr className="border-b border-line bg-surface-muted/60">
            {columnas.map((col) => (
              <th
                key={col.clave}
                scope="col"
                className={cn(
                  'px-3 py-2 text-caption font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400',
                  ALINEACION[col.alineacion ?? 'izquierda'],
                  col.anchoClase,
                  col.ocultaEnMovil && 'hidden sm:table-cell'
                )}
              >
                {col.titulo}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filas.map((fila, i) => (
            <tr
              key={claveDeFila(fila, i)}
              onClick={onFilaClick ? () => onFilaClick(fila) : undefined}
              className={cn(
                'border-b border-line last:border-0',
                filaDestacada?.(fila) && 'bg-warning-50 dark:bg-warning-500/15',
                onFilaClick && 'cursor-pointer hover:bg-surface-muted/70'
              )}
            >
              {/* Las celdas van en el MISMO orden que las cabeceras. Pintar primero las
                  visibles y después las ocultas desalinearía la tabla en cuanto una columna
                  escondida quedara entre dos visibles. */}
              {columnas.map((col) => (
                <td
                  key={col.clave}
                  className={cn(
                    'px-3 py-2 align-top',
                    ALINEACION[col.alineacion ?? 'izquierda'],
                    col.ocultaEnMovil && 'hidden sm:table-cell'
                  )}
                >
                  {valor(col, fila, i)}
                  {/* En móvil, lo que se escondió baja aquí en vez de desaparecer. */}
                  {col === visiblesMovil[0] && plegadasMovil.length > 0 && (
                    <dl className="mt-1 space-y-0.5 sm:hidden">
                      {plegadasMovil.map((oculta) => (
                        <div key={oculta.clave} className="flex gap-1 text-caption text-neutral-500 dark:text-neutral-400">
                          <dt className="font-medium">{oculta.titulo}:</dt>
                          <dd>{valor(oculta, fila, i)}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </>
  )
}

export default DataTable
